import { ComponentChildren, FunctionComponent } from 'preact';
import { IcoCheck, IcoChevronDown } from '@components/icons';
import { useEffect, useLayoutEffect, useRef, useState } from 'preact/hooks';
import { FILTER_CONTROL_CLASS, filterControlState, filterIconState } from './filter-control';

type MenuControls = {
  matches(text: string): boolean;
  choose(apply: () => void): void;
  close(): void;
};

export function FilterMenu({
  id,
  icon: Icon,
  label,
  active,
  triggerLabel,
  menuLabel,
  searchLabel,
  showSearch = true,
  onOpen,
  children,
}: {
  id?: string;
  icon: FunctionComponent<{ class?: string }>;
  label: string;
  active: boolean;
  triggerLabel: string;
  menuLabel: string;
  searchLabel: string;
  showSearch?: boolean;
  onOpen?(): void;
  children(controls: MenuControls): ComponentChildren;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const root = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const searchInput = useRef<HTMLInputElement>(null);
  const popup = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    if (!open) {
      return;
    }
    function clampPopup() {
      const el = popup.current;
      if (!el) {
        return;
      }
      el.style.transform = '';
      el.style.maxWidth = '';
      const inset = 16;
      let left = inset;
      let right = window.innerWidth - inset;
      for (let ancestor = el.parentElement; ancestor; ancestor = ancestor.parentElement) {
        const { overflowX } = getComputedStyle(ancestor);
        if (!/^(auto|scroll|hidden|clip)$/.test(overflowX)) {
          continue;
        }
        const bounds = ancestor.getBoundingClientRect();
        const clientLeft = bounds.left + ancestor.clientLeft;
        left = Math.max(left, clientLeft);
        right = Math.min(right, clientLeft + ancestor.clientWidth);
      }
      const available = Math.max(1, right - left);
      let bounds = el.getBoundingClientRect();
      if (bounds.width > available) {
        el.style.maxWidth = `${available}px`;
        bounds = el.getBoundingClientRect();
      }
      const delta =
        bounds.left < left ? left - bounds.left : bounds.right > right ? right - bounds.right : 0;
      if (delta) {
        el.style.transform = `translateX(${delta}px)`;
      }
    }
    clampPopup();
    window.addEventListener('resize', clampPopup);
    return () => window.removeEventListener('resize', clampPopup);
  }, [open, label]);
  function close(focus = false) {
    setOpen(false);
    setSearch('');
    if (focus) {
      requestAnimationFrame(() => trigger.current?.focus());
    }
  }
  useEffect(() => {
    if (open && showSearch && document.activeElement === popup.current?.querySelector('button')) {
      searchInput.current?.focus();
    }
  }, [showSearch]);
  useEffect(() => {
    if (!open) {
      return;
    }
    (searchInput.current ?? popup.current?.querySelector<HTMLElement>('button'))?.focus();
    function dismiss(event: Event) {
      if (!root.current?.contains(event.target as Node)) {
        close();
      }
    }
    function key(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        close(true);
      }
    }
    document.addEventListener('pointerdown', dismiss);
    document.addEventListener('focusin', dismiss);
    document.addEventListener('keydown', key, true);
    return () => {
      document.removeEventListener('pointerdown', dismiss);
      document.removeEventListener('focusin', dismiss);
      document.removeEventListener('keydown', key, true);
    };
  }, [open]);
  return (
    <div ref={root} class="relative w-full min-w-0 sm:w-auto">
      <button
        ref={trigger}
        id={id}
        type="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={triggerLabel}
        onClick={() => {
          if (!open) {
            onOpen?.();
          }
          setOpen(!open);
        }}
        class={`inline-flex w-full min-w-0 items-center sm:w-auto sm:max-w-72 ${FILTER_CONTROL_CLASS} ${filterControlState(active)}`}
      >
        <Icon class={`size-4 shrink-0 ${filterIconState(active)}`} />
        <span class="min-w-0 flex-1 truncate text-left" title={label}>
          {label}
        </span>
        <IcoChevronDown class={`size-4 shrink-0 ${filterIconState(active)}`} />
      </button>
      {open && (
        <div
          ref={popup}
          role="dialog"
          aria-label={menuLabel}
          class="absolute left-0 top-full z-30 mt-2 w-full max-w-[calc(100vw-2rem)] rounded-lg border border-gray-200 bg-white p-2 shadow-xl sm:right-0 sm:left-auto sm:w-72"
        >
          {showSearch && (
            <input
              ref={searchInput}
              type="search"
              aria-label={searchLabel}
              placeholder={searchLabel}
              class="ruz-input mb-1 w-full text-sm"
              value={search}
              onInput={(e) => setSearch(e.currentTarget.value)}
            />
          )}
          {children({
            matches: (text) => !showSearch || text.toLowerCase().includes(search.toLowerCase()),
            choose(apply) {
              apply();
              close(true);
            },
            close,
          })}
        </div>
      )}
    </div>
  );
}

export function FilterOption({
  selected,
  label,
  count,
  onClick,
}: {
  selected: boolean;
  label: string;
  count?: number;
  onClick(): void;
}) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onClick}
      class={`flex min-h-11 w-full items-center gap-2 rounded-md px-2 text-left text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 ${selected ? 'bg-indigo-50 font-medium text-indigo-800' : 'text-gray-700 hover:bg-indigo-50'}`}
    >
      <span class="min-w-0 flex-1 truncate" title={label}>
        {label}
      </span>
      {count !== undefined && (
        <span
          class={`shrink-0 text-xs tabular-nums ${selected ? 'text-indigo-700' : 'text-gray-500'}`}
        >
          <span class="sr-only">, </span>
          {count}
          <span class="sr-only"> {count === 1 ? 'file' : 'files'}</span>
        </span>
      )}
      {selected ? (
        <IcoCheck class="size-4 shrink-0 text-indigo-700" />
      ) : (
        count !== undefined && <span class="size-4 shrink-0" />
      )}
    </button>
  );
}
